import React, { FunctionComponent, useEffect, useState } from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorSecond,
  red,
  white,
} from '../../assets/styledAssets/vars';
import Button from '../../components/Button/Button.component';
import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
} from '../../components/Icons/Icons.component';
import { GridColumn } from '@progress/kendo-react-grid';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import WorldRecordDetails from './components/WorldRecordDetails/WorldRecordDetails.component';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';
import { WorldRank, WorldRecord, WorldRecordType } from './worldrecords';
import AchievementsService from '../../service/Achievements.service';
import { convertToTimeMask } from '../../helpers/app.helpers';
import { WorldRankValue } from './constants';

const connector = connect((state: RootState) => ({}), {});

type PropsFromRedux = ConnectedProps<typeof connector>;

const WorldRecords: FunctionComponent<PropsFromRedux> = () => {
  const intl = useIntl();
  const [list, setList] = useState<WorldRecord[]>([]);
  const [showAddRecord, setShowAddRecord] = useState<boolean>();
  const [activeRecord, setActiveRecord] = useState<WorldRecord>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [confirmationHandler, setConfirmationHandler] = useState<() => void>();

  useEffect(() => {
    AchievementsService.fetchWorldRecords().then((res) =>
      setList(
        res.items.filter((item) => item.event.input !== WorldRecordType.Rank),
      ),
    );
  }, []);

  const removeRecord = (id: string) => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.deleteWorldRecord));
    setConfirmationHandler(() => {
      return () => {
        AchievementsService.deleteWorldRecord(id).then(() => {
          setList(list.filter((t) => t.id !== id));
        });
      };
    });
  };

  const addRecord = (record: WorldRecord) => {
    if (activeRecord?.id) {
      AchievementsService.updateWorldRecord(record).then((res) => {
        setList((list) => {
          let oldRecord =
            list.find((t) => t.id === record.id) || ({} as WorldRecord);
          Object.assign(oldRecord, record);
          return [...list];
        });
        setShowAddRecord(false);
        setActiveRecord(undefined);
      });
    } else {
      AchievementsService.createWorldRecord(record).then((res) => {
        setList(list.concat([res]));
        setShowAddRecord(false);
      });
    }
  };

  return (
    <Wrap
      flexDir={'column'}
      flexWrap={'nowrap'}
      overflow={['auto', 'unset']}
      width={'100%'}
    >
      <Wrap flexDir={'column'} width={'100%'}>
        <Wrap
          flexDir={['column', 'row']}
          alignItems={['flex-start', 'center']}
          justifyCont={['flex-start', 'space-between']}
          width={'100%'}
        >
          <Wrap
            alignItems={['flex-start', 'flex-end']}
            flexDir={['column', 'row']}
          >
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[
                [{ side: 'right', color: 'transparent' }],
                [{ side: 'right', color: accentColorSecond }],
              ]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 1rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(messages.title)}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {list.length} {intl.formatMessage(messages.total)}
            </Wrap>
          </Wrap>
          <Wrap
            alignItems={'center'}
            margin={['2rem 0', '0']}
            justifyCont={['flex-start', 'flex-end']}
          >
            <Button primary onClick={() => setShowAddRecord(true)}>
              <PlusIcon color={white} width={'1.4rem'} />
              {intl.formatMessage(messages.addWorldRecord)}
            </Button>
          </Wrap>
        </Wrap>
      </Wrap>
      <Wrap
        grow={'1'}
        overflow={'hidden'}
        width={'100%'}
        height={'100%'}
        hiddenXS
      >
        <StyledTable style={{ height: '100%', width: '100%' }} data={list}>
          <GridColumn
            field="event.name"
            title={intl.formatMessage(messages.name)}
          />
          <GridColumn
            field="men"
            title={intl.formatMessage(messages.menValue)}
            cell={(props) => (
              <td>
                {props.dataItem.event.input === WorldRecordType.Time
                  ? convertToTimeMask(props.dataItem.men)
                  : props.dataItem.event.input === WorldRecordType.Rank
                  ? WorldRankValue[props.dataItem.men as WorldRank]
                  : props.dataItem.men}
              </td>
            )}
          />
          <GridColumn
            field="women"
            title={intl.formatMessage(messages.womenValue)}
            cell={(props) => (
              <td>
                {props.dataItem.event.input === WorldRecordType.Time
                  ? convertToTimeMask(props.dataItem.women)
                  : props.dataItem.event.input === WorldRecordType.Rank
                  ? WorldRankValue[props.dataItem.women as WorldRank]
                  : props.dataItem.women}
              </td>
            )}
          />
          <GridColumn
            width={'90px'}
            cell={(props) => (
              <StyledTD>
                <Wrap>
                  <Wrap>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        setActiveRecord(props.dataItem);
                        setShowAddRecord(true);
                      }}
                    >
                      <EditIcon color={accentColorPrim} width={'2rem'} />
                    </Button>
                  </Wrap>
                  <Wrap margin={'0 0 0 1rem'}>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        removeRecord(props.dataItem.id);
                      }}
                    >
                      <DeleteIcon color={red} width={'2rem'} />
                    </Button>
                  </Wrap>
                </Wrap>
              </StyledTD>
            )}
          />
        </StyledTable>
      </Wrap>

      <Wrap
        width={'100%'}
        flexDir={'column'}
        overflow={'auto'}
        flexWrap={'nowrap'}
        hiddenSM
      >
        <Wrap width={'100%'}>
          {list.map((item, index) => (
            <Wrap
              margin={'0 0 2rem 0'}
              width={'100%'}
              padding={'3rem'}
              br={'1.2rem'}
              bgColor={white}
              key={`card-${index}`}
              flexDir={'column'}
            >
              <Wrap
                grow={'1'}
                alignItems={'center'}
                justifyCont={'space-between'}
                width={'100%'}
              >
                <Wrap fw={'bold'} fs={'2rem'}>
                  {item.event.name}
                </Wrap>
                <Wrap>
                  {/*                  <Wrap>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        setActiveRecord(props.dataItem);
                        setShowAddRecord(true);
                      }}
                    >
                      <EditIcon color={accentColorPrim} width={'2rem'} />
                    </Button>
                  </Wrap>*/}
                  <Wrap margin={'0 0 0 1rem'}>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        removeRecord(item.id as string);
                      }}
                    >
                      <DeleteIcon color={red} width={'2rem'} />
                    </Button>
                  </Wrap>
                </Wrap>
              </Wrap>
              <Wrap width={'100%'} alignItems={'center'}>
                <Wrap width={'calc(50% - 1rem)'} flexDir={'column'}>
                  <Wrap color={fontColorSecond}>
                    {intl.formatMessage(messages.menValue)}
                  </Wrap>
                  <Wrap fw={'bold'}>
                    {item.event.input === WorldRecordType.Time
                      ? convertToTimeMask(item.men)
                      : item.event.input === WorldRecordType.Rank
                      ? WorldRankValue[item.men as WorldRank]
                      : item.men}
                  </Wrap>
                </Wrap>
                <Wrap width={'calc(50% - 1rem)'} flexDir={'column'}>
                  <Wrap color={fontColorSecond}>
                    {intl.formatMessage(messages.womenValue)}
                  </Wrap>
                  <Wrap fw={'bold'}>
                    {item.event.input === WorldRecordType.Time
                      ? convertToTimeMask(item.women)
                      : item.event.input === WorldRecordType.Rank
                      ? WorldRankValue[item.women as WorldRank]
                      : item.women}
                  </Wrap>
                </Wrap>
              </Wrap>
              <Wrap color={fontColorSecond} margin={'2rem 0 0 0'}>
                {item.id}
              </Wrap>
            </Wrap>
          ))}
        </Wrap>
      </Wrap>
      {showAddRecord && (
        <DialogWrap
          title={intl.formatMessage(
            activeRecord?.id
              ? messages.editWorldRecord
              : messages.addWorldRecord,
          )}
          children={
            <WorldRecordDetails
              onSave={addRecord}
              onCancel={() => {
                setShowAddRecord(false);
                setActiveRecord(undefined);
              }}
              worldRecord={activeRecord}
            />
          }
        />
      )}
      {showConfirmation && confirmationHandler && (
        <ConfirmationDialog
          bodyText={confirmationText}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={confirmationHandler}
        />
      )}
    </Wrap>
  );
};

export default connector(WorldRecords);
